/**
 * Daily statistics breakdown table used by CompanyStatisticsPage.
 * Renders a loading skeleton, a data table, or an "empty" alert depending on state.
 * Eliminates the internal duplication of the identical table header/skeleton structure
 * that previously appeared twice (once for loading, once for data).
 */

import React from 'react';
import {
  Alert,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { formatNumber } from '../../utils/numberFormat';

interface Props {
  loading: boolean;
  data: Array<any>;
}

const StatsDailyBreakdownTable: React.FC<Props> = ({ loading, data }) => {
  const columns = (
    <TableRow>
      <TableCell>Date</TableCell>
      <TableCell align="right">Transactions</TableCell>
      <TableCell align="right">Geolocations</TableCell>
      <TableCell align="right">Unique Shipments</TableCell>
    </TableRow>
  );

  if (loading) {
    return (
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>{columns}</TableHead>
          <TableBody>
            {Array.from({ length: 10 }, (_, i) => `daily-skeleton-${i}`).map((rowKey) => (
              <TableRow key={rowKey}>
                <TableCell><Skeleton variant="text" width={120} /></TableCell>
                <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
                <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
                <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (data.length > 0) {
    return (
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>{columns}</TableHead>
          <TableBody>
            {data.map((day: any) => (
              <TableRow key={day.day} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {format(new Date(day.day), 'MMM dd, yyyy')}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatNumber(day.total_transactions)}</TableCell>
                <TableCell align="right">{formatNumber(day.total_geolocations)}</TableCell>
                <TableCell align="right">{formatNumber(day.total_unique_shipments)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return <Alert severity="info">No daily data available for this month</Alert>;
};

export default StatsDailyBreakdownTable;
